// Coach Analytics-Deepdive — Tile 12: personalised reports, export & saved templates.
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bookmark, Download, Trash2, FileText, Grid3x3, Table, Check, Loader2 } from "lucide-react";
import { downloadReport } from "./shared";

const REPORT_TILES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export function ReportsTile({ period }) {
  const t = useT();
  const [selected, setSelected] = useState(REPORT_TILES.slice());
  const [busy, setBusy] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplFmt, setTplFmt] = useState("pdf");
  const [savingTpl, setSavingTpl] = useState(false);
  const allOn = selected.length === REPORT_TILES.length;

  const toggle = (n) => setSelected((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b)));

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/coach/analytics-v2/report-templates");
      setTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch { setTemplates([]); }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const dl = async (kind, tiles = selected, per = period) => {
    if (tiles.length === 0) return;
    setBusy(kind);
    await downloadReport(`/coach/analytics-v2/report.${kind}.custom?period=${per}&tiles=${tiles.join(",")}`, `mywithin-analytics-${per}.${kind}`, t);
    setBusy(null);
  };

  const saveTemplate = async () => {
    const name = tplName.trim();
    if (!name || selected.length === 0) return;
    setSavingTpl(true);
    try {
      await api.post("/coach/analytics-v2/report-templates", { name, tiles: selected, format: tplFmt, period });
      setTplName("");
      await loadTemplates();
      toast.success(t("an.t12_tpl_saved"));
    } catch (e) { toast.error(apiError(e)); }
    setSavingTpl(false);
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/coach/analytics-v2/report-templates/${id}`); await loadTemplates(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <p className="mb-3 text-sm text-muted-foreground">{t("an.t12_hint")}</p>

      {/* Saved templates */}
      <div className="mb-4 rounded-xl border border-border bg-secondary/40 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Bookmark className="h-3.5 w-3.5 text-primary" /> {t("an.t12_templates")}</p>
        {templates.length === 0 ? (
          <p className="text-xs italic text-muted-foreground">{t("an.t12_templates_none")}</p>
        ) : (
          <div className="space-y-1.5">
            {templates.map((tpl) => (
              <div key={tpl.id} data-testid={`an-tpl-${tpl.id}`} className="flex items-center gap-2 rounded-lg border border-border bg-card p-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{tpl.name}</p>
                  <p className="text-xs text-muted-foreground">{(tpl.tiles || []).length} · {(tpl.format || "pdf").toUpperCase()}{tpl.period ? ` · ${tpl.period}` : ""}</p>
                </div>
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full" disabled={busy !== null} onClick={() => dl(tpl.format, tpl.tiles, tpl.period || period)} data-testid={`an-tpl-run-${tpl.id}`}>
                  <Download className="h-3.5 w-3.5" /> {t("an.t12_template_run")}
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" onClick={() => deleteTemplate(tpl.id)} data-testid={`an-tpl-del-${tpl.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tile checklist */}
      <div className="mb-3 rounded-xl border border-border p-3">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("an.t12_pick")}</p>
          <button onClick={() => setSelected(allOn ? [] : REPORT_TILES.slice())} data-testid="an-pick-toggle-all" className="text-xs font-medium text-primary hover:underline">
            {allOn ? t("an.t12_all_off") : t("an.t12_all_on")}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {REPORT_TILES.map((n) => {
            const on = selected.includes(n);
            return (
              <button key={n} onClick={() => toggle(n)} data-testid={`an-pick-tile-${n}`} className={`flex items-center gap-2 rounded-lg border p-2 text-left ${on ? "border-primary bg-primary/10" : "border-border"}`}>
                <span className={`flex h-4 w-4 items-center justify-center rounded ${on ? "bg-primary text-primary-foreground" : "border border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
                <span className="text-xs font-medium text-foreground">{n}</span>
                <span className="truncate text-xs text-muted-foreground">{t(`an.t${n}`)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Save as template */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={tplName} onChange={(e) => setTplName(e.target.value)} maxLength={80} placeholder={t("an.t12_tpl_name_ph")} className="h-9 flex-1" data-testid="an-tpl-name" />
        {["pdf", "csv", "xlsx"].map((f) => (
          <button key={f} onClick={() => setTplFmt(f)} data-testid={`an-tpl-fmt-${f}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${tplFmt === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{f.toUpperCase()}</button>
        ))}
        <Button size="sm" className="h-9 gap-1.5 rounded-full" disabled={savingTpl || !tplName.trim() || selected.length === 0} onClick={saveTemplate} data-testid="an-tpl-save">
          {savingTpl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bookmark className="h-3.5 w-3.5" />} {t("an.t12_tpl_save")}
        </Button>
      </div>

      {/* Export buttons */}
      <div className="flex flex-wrap gap-2">
        <Button className="gap-1.5 rounded-full" disabled={busy === "pdf" || selected.length === 0} onClick={() => dl("pdf")} data-testid="an-dl-pdf">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t("an.t12_pdf")}
        </Button>
        <Button variant="outline" className="gap-1.5 rounded-full" disabled={busy === "csv" || selected.length === 0} onClick={() => dl("csv")} data-testid="an-dl-csv">
          {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Grid3x3 className="h-4 w-4" />} {t("an.t12_csv")}
        </Button>
        <Button variant="outline" className="gap-1.5 rounded-full" disabled={busy === "xlsx" || selected.length === 0} onClick={() => dl("xlsx")} data-testid="an-dl-xlsx">
          {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Table className="h-4 w-4" />} {t("an.t12_xlsx")}
        </Button>
      </div>
    </div>
  );
}
